/**
 * TanStack Query Configuration & Utilities
 *
 * This module provides:
 * - Centralized QueryClient configuration
 * - Error handling integration with microservices
 * - Optimistic update helpers
 * - Retry configuration for different error types
 * - Query key factories for type-safe caching
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  parseApiError,
  ApiError,
  ApiErrorCode,
  isInfrastructureError,
  isAuthError,
} from './api-errors';

// =============================================================================
// QUERY CLIENT CONFIGURATION
// =============================================================================

/**
 * Default stale time for different query categories (in milliseconds)
 */
export const STALE_TIMES = {
  // User/auth data - refresh frequently
  auth: 5 * 60 * 1000, // 5 minutes

  // Reference data that rarely changes
  static: 60 * 60 * 1000, // 1 hour

  // List data that may change frequently
  list: 30 * 1000, // 30 seconds

  // Detail data
  detail: 60 * 1000, // 1 minute

  // Dashboard/aggregated data
  dashboard: 2 * 60 * 1000, // 2 minutes

  // Real-time data
  realtime: 0, // Always stale
};

/**
 * Determine if a query should be retried based on the error
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const apiError = error instanceof ApiError ? error : parseApiError(error);

  // Don't retry auth errors - user needs to re-authenticate
  if (isAuthError(apiError)) {
    return false;
  }

  // Don't retry non-retryable errors
  if (!apiError.retryable) {
    return false;
  }

  // Respect one server-directed retry without creating a synchronized retry storm.
  if (apiError.code === ApiErrorCode.RATE_LIMITED) {
    return failureCount < 1;
  }

  // Retry infrastructure errors up to 3 times
  if (isInfrastructureError(apiError)) {
    return failureCount < 3;
  }

  // Default: retry up to 2 times
  return failureCount < 2;
}

/**
 * Calculate retry delay with exponential backoff
 */
export function calculateRetryDelay(attemptIndex: number, error: unknown): number {
  const apiError = error instanceof ApiError ? error : parseApiError(error);

  // Use server-specified retry-after if available
  if (apiError.retryAfter) {
    return apiError.retryAfter * 1000 + Math.random() * 1000;
  }

  // Exponential backoff: 1s, 2s, 4s, 8s...
  const baseDelay = 1000;
  const delay = Math.min(baseDelay * Math.pow(2, attemptIndex), 30000);

  // Add jitter
  return delay + Math.random() * 1000;
}

/**
 * Global error handler for queries
 */
function handleQueryError(error: unknown): void {
  const apiError = error instanceof ApiError ? error : parseApiError(error);

  // Don't show toast for auth errors (handled by interceptor redirect)
  if (isAuthError(apiError)) {
    return;
  }

  // Show error toast for infrastructure errors
  if (isInfrastructureError(apiError)) {
    toast.error(apiError.userMessage, {
      description: apiError.service ? `Service: ${apiError.service}` : undefined,
      action: apiError.retryable
        ? {
            label: 'Retry',
            onClick: () => {
              // Trigger a refetch - this is handled by the component
            },
          }
        : undefined,
    });
    return;
  }

  // Log errors in development
  if (import.meta.env.DEV) {
    console.error('[Query Error]', apiError.toJSON());
  }
}

/**
 * Global error handler for mutations
 */
function handleMutationError(error: unknown): void {
  const apiError = error instanceof ApiError ? error : parseApiError(error);

  // 409 Conflict — optimistic locking / concurrent edit
  if (apiError.httpStatus === 409) {
    toast.error('Update Conflict', {
      description: 'This record was modified by another user. Please refresh and try again.',
      action: {
        label: 'Refresh',
        onClick: () => window.location.reload(),
      },
    });
    return; // Don't show generic error toast
  }

  // Always show toast for mutation errors (user initiated action)
  toast.error(apiError.userMessage, {
    description:
      apiError.code === ApiErrorCode.VALIDATION_ERROR
        ? 'Please check your input and try again.'
        : undefined,
  });

  // Log in development
  if (import.meta.env.DEV) {
    console.error('[Mutation Error]', apiError.toJSON());
  }
}

/**
 * Create the QueryClient with optimized settings for microservices
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleQueryError,
    }),
    mutationCache: new MutationCache({
      onError: handleMutationError,
    }),
    defaultOptions: {
      queries: {
        staleTime: STALE_TIMES.list,
        gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
        retry: shouldRetryQuery,
        retryDelay: calculateRetryDelay,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
        networkMode: 'offlineFirst',
        // Transform errors to ApiError
        throwOnError: false,
      },
      mutations: {
        retry: false, // Don't auto-retry mutations
        networkMode: 'offlineFirst',
        throwOnError: false,
      },
    },
  });
}

// Singleton instance for the app
export const queryClient = createQueryClient();

// =============================================================================
// QUERY KEY FACTORIES
// Type-safe query key management
// =============================================================================

export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
    permissions: () => [...queryKeys.auth.all, 'permissions'] as const,
  },

  // Employees
  employees: {
    all: ['employees'] as const,
    lists: () => [...queryKeys.employees.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.employees.lists(), filters] as const,
    details: () => [...queryKeys.employees.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.employees.details(), id] as const,
  },

  // Departments
  departments: {
    all: ['departments'] as const,
    lists: () => [...queryKeys.departments.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.departments.lists(), filters] as const,
    details: () => [...queryKeys.departments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.departments.details(), id] as const,
  },

  // Legal Entities / Companies
  legalEntities: {
    all: ['legalEntities'] as const,
    lists: () => [...queryKeys.legalEntities.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.legalEntities.lists(), filters] as const,
    details: () => [...queryKeys.legalEntities.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.legalEntities.details(), id] as const,
  },

  // CRM - Leads
  leads: {
    all: ['leads'] as const,
    lists: () => [...queryKeys.leads.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.leads.lists(), filters] as const,
    details: () => [...queryKeys.leads.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.leads.details(), id] as const,
    stats: () => [...queryKeys.leads.all, 'stats'] as const,
  },

  // CRM - Deals
  deals: {
    all: ['deals'] as const,
    lists: () => [...queryKeys.deals.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.deals.lists(), filters] as const,
    details: () => [...queryKeys.deals.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.deals.details(), id] as const,
    pipeline: () => [...queryKeys.deals.all, 'pipeline'] as const,
    stats: () => [...queryKeys.deals.all, 'stats'] as const,
  },

  // CRM - Contacts
  contacts: {
    all: ['contacts'] as const,
    lists: () => [...queryKeys.contacts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.contacts.lists(), filters] as const,
    details: () => [...queryKeys.contacts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contacts.details(), id] as const,
  },

  // CRM - Accounts
  accounts: {
    all: ['accounts'] as const,
    lists: () => [...queryKeys.accounts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.accounts.lists(), filters] as const,
    details: () => [...queryKeys.accounts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.accounts.details(), id] as const,
  },

  // Attendance
  attendance: {
    all: ['attendance'] as const,
    lists: () => [...queryKeys.attendance.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.attendance.lists(), filters] as const,
    today: () => [...queryKeys.attendance.all, 'today'] as const,
    summary: (period: string) => [...queryKeys.attendance.all, 'summary', period] as const,
  },

  // Leaves
  leaves: {
    all: ['leaves'] as const,
    lists: () => [...queryKeys.leaves.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.leaves.lists(), filters] as const,
    details: () => [...queryKeys.leaves.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.leaves.details(), id] as const,
    balance: (employeeId: string) => [...queryKeys.leaves.all, 'balance', employeeId] as const,
  },

  // Payroll
  payroll: {
    all: ['payroll'] as const,
    // Payroll Runs
    runs: () => [...queryKeys.payroll.all, 'runs'] as const,
    runList: (filters: Record<string, unknown>) => [...queryKeys.payroll.runs(), filters] as const,
    runDetail: (id: string) => [...queryKeys.payroll.runs(), id] as const,
    runResults: (runId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.payroll.runs(), runId, 'results', filters ?? {}] as const,
    retroTriggers: (filters?: Record<string, unknown>) =>
      [...queryKeys.payroll.runs(), 'retro-triggers', filters ?? {}] as const,
    accountingPosting: (runId: string) =>
      [...queryKeys.payroll.runs(), runId, 'accounting-posting'] as const,
    accountingPostingRelease: (runId: string) =>
      [...queryKeys.payroll.runs(), runId, 'accounting-posting-release'] as const,
    accountingDefaults: () => [...queryKeys.payroll.config(), 'accounting-defaults'] as const,
    accountingMappings: (filters?: Record<string, unknown>) =>
      [...queryKeys.payroll.config(), 'accounting-mappings', filters ?? {}] as const,
    // Payslips
    payslips: () => [...queryKeys.payroll.all, 'payslips'] as const,
    payslipList: (filters: Record<string, unknown>) =>
      [...queryKeys.payroll.payslips(), filters] as const,
    payslipDetail: (id: string) => [...queryKeys.payroll.payslips(), id] as const,
    myPayslips: (employeeId: string) =>
      [...queryKeys.payroll.payslips(), 'my', employeeId] as const,
    // Config
    config: () => [...queryKeys.payroll.all, 'config'] as const,
    // Settlements
    settlement: (id: string) => [...queryKeys.payroll.all, 'settlement', id] as const,
    settlementHandoffQueue: () => [...queryKeys.payroll.all, 'settlement-handoff-queue'] as const,
    // Tax
    taxDeclarations: () => [...queryKeys.payroll.all, 'tax-declarations'] as const,
    taxDeclaration: (employeeId: string, fy: string) =>
      [...queryKeys.payroll.taxDeclarations(), employeeId, fy] as const,
    taxSlabs: (fy: string, regime?: string) =>
      [...queryKeys.payroll.all, 'tax-slabs', fy, regime ?? 'all'] as const,
    taxCalculation: (employeeId: string) =>
      [...queryKeys.payroll.all, 'tax-calculation', employeeId] as const,
    yearEndStatement: (employeeId: string, fy: string) =>
      [...queryKeys.payroll.all, 'year-end-statement', employeeId, fy] as const,
    // Components
    components: () => [...queryKeys.payroll.all, 'components'] as const,
    component: (code: string) => [...queryKeys.payroll.components(), code] as const,
    // Employee Salaries
    employeeSalary: (employeeId: string) =>
      [...queryKeys.payroll.all, 'salary', employeeId] as const,
    salaryHistory: (employeeId: string) =>
      [...queryKeys.payroll.all, 'salary-history', employeeId] as const,
    statutoryProfile: (employeeId: string) =>
      [...queryKeys.payroll.all, 'statutory-profile', employeeId] as const,
    statutoryEmployees: (fy: string) =>
      [...queryKeys.payroll.all, 'statutory-employees', fy] as const,
    // Existing payroll screens use these list/detail cache keys until payroll closeout.
    lists: () => [...queryKeys.payroll.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.payroll.lists(), filters] as const,
    details: () => [...queryKeys.payroll.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.payroll.details(), id] as const,
    summary: (period: string) => [...queryKeys.payroll.all, 'summary', period] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    overview: () => [...queryKeys.dashboard.all, 'overview'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    recent: () => [...queryKeys.dashboard.all, 'recent'] as const,
  },

  // Tenants (admin)
  tenants: {
    all: ['tenants'] as const,
    lists: () => [...queryKeys.tenants.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tenants.details(), id] as const,
  },

  // Performance Management
  performance: {
    all: ['performance'] as const,
    // Review Cycles
    cycles: {
      all: () => [...queryKeys.performance.all, 'cycles'] as const,
      lists: () => [...queryKeys.performance.cycles.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.performance.cycles.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.performance.cycles.all(), id] as const,
      current: () => [...queryKeys.performance.cycles.all(), 'current'] as const,
    },
    // Reviews
    reviews: {
      all: () => [...queryKeys.performance.all, 'reviews'] as const,
      lists: () => [...queryKeys.performance.reviews.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.performance.reviews.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.performance.reviews.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.performance.reviews.all(), 'employee', employeeId] as const,
      reviewer: (reviewerId: string) =>
        [...queryKeys.performance.reviews.all(), 'reviewer', reviewerId] as const,
    },
    // Goals & OKRs
    goals: {
      all: () => [...queryKeys.performance.all, 'goals'] as const,
      lists: () => [...queryKeys.performance.goals.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.performance.goals.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.performance.goals.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.performance.goals.all(), 'employee', employeeId] as const,
      keyResults: (goalId: string) =>
        [...queryKeys.performance.goals.all(), goalId, 'key-results'] as const,
    },
    // 360 Feedback
    feedback: {
      all: () => [...queryKeys.performance.all, 'feedback'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.performance.feedback.all(), filters] as const,
      detail: (id: string) => [...queryKeys.performance.feedback.all(), id] as const,
      review: (reviewId: string) =>
        [...queryKeys.performance.feedback.all(), 'review', reviewId] as const,
      myRequests: () => [...queryKeys.performance.feedback.all(), 'my-requests'] as const,
    },
    // Competencies
    competencies: {
      all: () => [...queryKeys.performance.all, 'competencies'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.performance.competencies.all(), filters] as const,
      detail: (id: string) => [...queryKeys.performance.competencies.all(), id] as const,
      scores: (reviewId: string) =>
        [...queryKeys.performance.competencies.all(), 'scores', reviewId] as const,
    },
    stats: () => [...queryKeys.performance.all, 'stats'] as const,
    employeeStats: (employeeId: string) =>
      [...queryKeys.performance.all, 'stats', 'employee', employeeId] as const,
    teamStats: (managerId: string) =>
      [...queryKeys.performance.all, 'stats', 'team', managerId] as const,
  },

  // Benefits Management
  benefits: {
    all: ['benefits'] as const,
    // Plans
    plans: {
      all: () => [...queryKeys.benefits.all, 'plans'] as const,
      lists: () => [...queryKeys.benefits.plans.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.benefits.plans.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.benefits.plans.all(), id] as const,
    },
    // Enrollments
    enrollments: {
      all: () => [...queryKeys.benefits.all, 'enrollments'] as const,
      lists: () => [...queryKeys.benefits.enrollments.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.benefits.enrollments.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.benefits.enrollments.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.benefits.enrollments.all(), 'employee', employeeId] as const,
    },
    // Beneficiaries
    beneficiaries: {
      all: () => [...queryKeys.benefits.all, 'beneficiaries'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.benefits.beneficiaries.all(), filters] as const,
      detail: (id: string) => [...queryKeys.benefits.beneficiaries.all(), id] as const,
      enrollment: (enrollmentId: string) =>
        [...queryKeys.benefits.beneficiaries.all(), 'enrollment', enrollmentId] as const,
    },
    // Claims
    claims: {
      all: () => [...queryKeys.benefits.all, 'claims'] as const,
      lists: () => [...queryKeys.benefits.claims.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.benefits.claims.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.benefits.claims.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.benefits.claims.all(), 'employee', employeeId] as const,
    },
    // Enrollment Periods
    periods: {
      all: () => [...queryKeys.benefits.all, 'periods'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.benefits.periods.all(), filters] as const,
      detail: (id: string) => [...queryKeys.benefits.periods.all(), id] as const,
      current: () => [...queryKeys.benefits.periods.all(), 'current'] as const,
    },
    stats: () => [...queryKeys.benefits.all, 'stats'] as const,
  },

  // Compliance Management
  compliance: {
    all: ['compliance'] as const,
    // Policies
    policies: {
      all: () => [...queryKeys.compliance.all, 'policies'] as const,
      lists: () => [...queryKeys.compliance.policies.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.compliance.policies.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.compliance.policies.all(), id] as const,
    },
    // Acknowledgments
    acknowledgments: {
      all: () => [...queryKeys.compliance.all, 'acknowledgments'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.compliance.acknowledgments.all(), filters] as const,
      employee: (employeeId: string) =>
        [...queryKeys.compliance.acknowledgments.all(), 'employee', employeeId] as const,
    },
    // Training
    training: {
      all: () => [...queryKeys.compliance.all, 'training'] as const,
      required: (employeeId: string) =>
        [...queryKeys.compliance.training.all(), 'required', employeeId] as const,
      completions: (filters: Record<string, unknown>) =>
        [...queryKeys.compliance.training.all(), 'completions', filters] as const,
    },
    // Certifications
    certifications: {
      all: () => [...queryKeys.compliance.all, 'certifications'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.compliance.certifications.all(), filters] as const,
      detail: (id: string) => [...queryKeys.compliance.certifications.all(), id] as const,
      expiring: () => [...queryKeys.compliance.certifications.all(), 'expiring'] as const,
    },
    // Audits
    audits: {
      all: () => [...queryKeys.compliance.all, 'audits'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.compliance.audits.all(), filters] as const,
      detail: (id: string) => [...queryKeys.compliance.audits.all(), id] as const,
    },
    // Incidents
    incidents: {
      all: () => [...queryKeys.compliance.all, 'incidents'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.compliance.incidents.all(), filters] as const,
      detail: (id: string) => [...queryKeys.compliance.incidents.all(), id] as const,
    },
    stats: () => [...queryKeys.compliance.all, 'stats'] as const,
    dashboard: () => [...queryKeys.compliance.all, 'dashboard'] as const,
  },

  // Learning & Development
  learning: {
    all: ['learning'] as const,
    // Courses
    courses: {
      all: () => [...queryKeys.learning.all, 'courses'] as const,
      lists: () => [...queryKeys.learning.courses.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.learning.courses.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.learning.courses.all(), id] as const,
      content: (courseId: string) =>
        [...queryKeys.learning.courses.all(), courseId, 'content'] as const,
    },
    // Enrollments
    enrollments: {
      all: () => [...queryKeys.learning.all, 'enrollments'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.learning.enrollments.all(), filters] as const,
      detail: (id: string) => [...queryKeys.learning.enrollments.all(), id] as const,
      my: () => [...queryKeys.learning.enrollments.all(), 'my'] as const,
    },
    // Quizzes
    quizzes: {
      all: () => [...queryKeys.learning.all, 'quizzes'] as const,
      course: (courseId: string) =>
        [...queryKeys.learning.quizzes.all(), 'course', courseId] as const,
      detail: (id: string) => [...queryKeys.learning.quizzes.all(), id] as const,
      attempts: (quizId: string) =>
        [...queryKeys.learning.quizzes.all(), quizId, 'attempts'] as const,
    },
    // Certificates
    certificates: {
      all: () => [...queryKeys.learning.all, 'certificates'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.learning.certificates.all(), filters] as const,
      detail: (id: string) => [...queryKeys.learning.certificates.all(), id] as const,
      my: () => [...queryKeys.learning.certificates.all(), 'my'] as const,
    },
    // Learning Paths
    paths: {
      all: () => [...queryKeys.learning.all, 'paths'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.learning.paths.all(), filters] as const,
      detail: (id: string) => [...queryKeys.learning.paths.all(), id] as const,
    },
    // Skills
    skills: {
      all: () => [...queryKeys.learning.all, 'skills'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.learning.skills.all(), filters] as const,
      detail: (id: string) => [...queryKeys.learning.skills.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.learning.skills.all(), 'employee', employeeId] as const,
    },
    // Training Requests
    requests: {
      all: () => [...queryKeys.learning.all, 'requests'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.learning.requests.all(), filters] as const,
      detail: (id: string) => [...queryKeys.learning.requests.all(), id] as const,
    },
    analytics: () => [...queryKeys.learning.all, 'analytics'] as const,
    learnerAnalytics: (learnerId: string) =>
      [...queryKeys.learning.all, 'analytics', 'learner', learnerId] as const,
  },

  // Onboarding
  onboarding: {
    all: ['onboarding'] as const,
    // Templates
    templates: {
      all: () => [...queryKeys.onboarding.all, 'templates'] as const,
      lists: () => [...queryKeys.onboarding.templates.all(), 'list'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.onboarding.templates.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.onboarding.templates.all(), id] as const,
    },
    documentTemplates: {
      all: () => [...queryKeys.onboarding.all, 'document-templates'] as const,
      lists: () => [...queryKeys.onboarding.documentTemplates.all(), 'list'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.onboarding.documentTemplates.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.onboarding.documentTemplates.all(), id] as const,
      preview: (id: string, employeeId: string) =>
        [...queryKeys.onboarding.documentTemplates.detail(id), 'preview', employeeId] as const,
    },
    // Sessions
    sessions: {
      all: () => [...queryKeys.onboarding.all, 'sessions'] as const,
      lists: () => [...queryKeys.onboarding.sessions.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.onboarding.sessions.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.onboarding.sessions.all(), id] as const,
      my: () => [...queryKeys.onboarding.sessions.all(), 'my'] as const,
    },
    // Tasks
    tasks: {
      all: () => [...queryKeys.onboarding.all, 'tasks'] as const,
      session: (sessionId: string) =>
        [...queryKeys.onboarding.tasks.all(), 'session', sessionId] as const,
      detail: (sessionId: string, taskId: string) =>
        [...queryKeys.onboarding.tasks.session(sessionId), taskId] as const,
    },
    // Documents
    documents: {
      all: () => [...queryKeys.onboarding.all, 'documents'] as const,
      session: (sessionId: string) =>
        [...queryKeys.onboarding.documents.all(), 'session', sessionId] as const,
    },
  },

  // Offboarding
  offboarding: {
    all: ['offboarding'] as const,
    // Cases
    cases: {
      all: () => [...queryKeys.offboarding.all, 'cases'] as const,
      lists: () => [...queryKeys.offboarding.cases.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.offboarding.cases.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.offboarding.cases.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.offboarding.cases.all(), 'employee', employeeId] as const,
    },
    // Exit Interviews
    exitInterviews: {
      all: () => [...queryKeys.offboarding.all, 'exit-interviews'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.offboarding.exitInterviews.all(), filters] as const,
      detail: (id: string) => [...queryKeys.offboarding.exitInterviews.all(), id] as const,
    },
    // Clearance
    clearance: {
      all: () => [...queryKeys.offboarding.all, 'clearance'] as const,
      case: (caseId: string) => [...queryKeys.offboarding.clearance.all(), 'case', caseId] as const,
    },
    // Knowledge Transfer
    knowledgeTransfer: {
      all: () => [...queryKeys.offboarding.all, 'knowledge-transfer'] as const,
      case: (caseId: string) =>
        [...queryKeys.offboarding.knowledgeTransfer.all(), 'case', caseId] as const,
    },
    // Final Settlement
    settlement: {
      all: () => [...queryKeys.offboarding.all, 'settlement'] as const,
      case: (caseId: string) =>
        [...queryKeys.offboarding.settlement.all(), 'case', caseId] as const,
    },
    stats: () => [...queryKeys.offboarding.all, 'stats'] as const,
  },

  // Succession Planning
  succession: {
    all: ['succession'] as const,
    // Key Positions
    positions: {
      all: () => [...queryKeys.succession.all, 'positions'] as const,
      lists: () => [...queryKeys.succession.positions.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.succession.positions.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.succession.positions.all(), id] as const,
    },
    // Candidates
    candidates: {
      all: () => [...queryKeys.succession.all, 'candidates'] as const,
      position: (positionId: string) =>
        [...queryKeys.succession.candidates.all(), 'position', positionId] as const,
      detail: (id: string) => [...queryKeys.succession.candidates.all(), id] as const,
    },
    // Development Plans
    developmentPlans: {
      all: () => [...queryKeys.succession.all, 'development-plans'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.succession.developmentPlans.all(), filters] as const,
      detail: (id: string) => [...queryKeys.succession.developmentPlans.all(), id] as const,
      candidate: (candidateId: string) =>
        [...queryKeys.succession.developmentPlans.all(), 'candidate', candidateId] as const,
    },
    // Talent Pools
    talentPools: {
      all: () => [...queryKeys.succession.all, 'talent-pools'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.succession.talentPools.all(), filters] as const,
      detail: (id: string) => [...queryKeys.succession.talentPools.all(), id] as const,
    },
    // 9-Box Grid
    nineBox: {
      all: () => [...queryKeys.succession.all, 'nine-box'] as const,
      grid: () => [...queryKeys.succession.nineBox.all(), 'grid'] as const,
      employee: (employeeId: string) =>
        [...queryKeys.succession.nineBox.all(), 'employee', employeeId] as const,
    },
    // Risk Assessment
    risk: {
      all: () => [...queryKeys.succession.all, 'risk'] as const,
      position: (positionId: string) =>
        [...queryKeys.succession.risk.all(), 'position', positionId] as const,
    },
    stats: () => [...queryKeys.succession.all, 'stats'] as const,
  },

  // Travel Management
  travel: {
    all: ['travel'] as const,
    // Requests
    requests: {
      all: () => [...queryKeys.travel.all, 'requests'] as const,
      lists: () => [...queryKeys.travel.requests.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.travel.requests.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.travel.requests.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.travel.requests.all(), 'employee', employeeId] as const,
    },
    // Itinerary
    itinerary: {
      all: () => [...queryKeys.travel.all, 'itinerary'] as const,
      request: (requestId: string) =>
        [...queryKeys.travel.itinerary.all(), 'request', requestId] as const,
    },
    // Accommodations
    accommodations: {
      all: () => [...queryKeys.travel.all, 'accommodations'] as const,
      request: (requestId: string) =>
        [...queryKeys.travel.accommodations.all(), 'request', requestId] as const,
    },
    // Expenses
    expenses: {
      all: () => [...queryKeys.travel.all, 'expenses'] as const,
      request: (requestId: string) =>
        [...queryKeys.travel.expenses.all(), 'request', requestId] as const,
    },
    stats: () => [...queryKeys.travel.all, 'stats'] as const,
  },

  // Reimbursements
  reimbursements: {
    all: ['reimbursements'] as const,
    // Expense Reports
    reports: {
      all: () => [...queryKeys.reimbursements.all, 'reports'] as const,
      lists: () => [...queryKeys.reimbursements.reports.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.reimbursements.reports.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.reimbursements.reports.all(), id] as const,
      employee: (employeeId: string) =>
        [...queryKeys.reimbursements.reports.all(), 'employee', employeeId] as const,
    },
    // Expense Items
    items: {
      all: () => [...queryKeys.reimbursements.all, 'items'] as const,
      report: (reportId: string) =>
        [...queryKeys.reimbursements.items.all(), 'report', reportId] as const,
    },
    // Categories
    categories: {
      all: () => [...queryKeys.reimbursements.all, 'categories'] as const,
      list: () => [...queryKeys.reimbursements.categories.all(), 'list'] as const,
    },
    stats: () => [...queryKeys.reimbursements.all, 'stats'] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.notifications.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.notifications.all, id] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
    templates: {
      all: () => [...queryKeys.notifications.all, 'templates'] as const,
      list: (channel?: string) => [...queryKeys.notifications.templates.all(), channel] as const,
      detail: (id: string) => [...queryKeys.notifications.templates.all(), id] as const,
    },
    channels: {
      all: () => [...queryKeys.notifications.all, 'channels'] as const,
      list: () => [...queryKeys.notifications.channels.all(), 'list'] as const,
    },
    scheduled: {
      all: () => [...queryKeys.notifications.all, 'scheduled'] as const,
      list: () => [...queryKeys.notifications.scheduled.all(), 'list'] as const,
    },
    logs: {
      all: () => [...queryKeys.notifications.all, 'logs'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.notifications.logs.all(), filters] as const,
    },
    stats: () => [...queryKeys.notifications.all, 'stats'] as const,
  },

  // CRM Extended
  crm: {
    all: ['crm'] as const,
    // Campaigns
    campaigns: {
      all: () => [...queryKeys.crm.all, 'campaigns'] as const,
      lists: () => [...queryKeys.crm.campaigns.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.crm.campaigns.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.crm.campaigns.all(), id] as const,
    },
    // Quotes
    quotes: {
      all: () => [...queryKeys.crm.all, 'quotes'] as const,
      lists: () => [...queryKeys.crm.quotes.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.crm.quotes.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.crm.quotes.all(), id] as const,
    },
    // Products
    products: {
      all: () => [...queryKeys.crm.all, 'products'] as const,
      lists: () => [...queryKeys.crm.products.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.crm.products.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.crm.products.all(), id] as const,
    },
    // Vendors
    vendors: {
      all: () => [...queryKeys.crm.all, 'vendors'] as const,
      lists: () => [...queryKeys.crm.vendors.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.crm.vendors.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.crm.vendors.all(), id] as const,
    },
    // Email Templates
    emailTemplates: {
      all: () => [...queryKeys.crm.all, 'email-templates'] as const,
      list: () => [...queryKeys.crm.emailTemplates.all(), 'list'] as const,
      detail: (id: string) => [...queryKeys.crm.emailTemplates.all(), id] as const,
    },
    // Activities
    activities: {
      all: () => [...queryKeys.crm.all, 'activities'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.crm.activities.all(), filters] as const,
      entity: (entityType: string, entityId: string) =>
        [...queryKeys.crm.activities.all(), entityType, entityId] as const,
    },
    stats: () => [...queryKeys.crm.all, 'stats'] as const,
  },

  // Admin / Feature Flags
  admin: {
    all: ['admin'] as const,
    featureFlags: {
      all: () => [...queryKeys.admin.all, 'feature-flags'] as const,
      list: () => [...queryKeys.admin.featureFlags.all(), 'list'] as const,
      detail: (key: string) => [...queryKeys.admin.featureFlags.all(), key] as const,
    },
    systemConfig: {
      all: () => [...queryKeys.admin.all, 'system-config'] as const,
      list: () => [...queryKeys.admin.systemConfig.all(), 'list'] as const,
      detail: (key: string) => [...queryKeys.admin.systemConfig.all(), key] as const,
    },
    auditLogs: {
      all: () => [...queryKeys.admin.all, 'audit-logs'] as const,
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.admin.auditLogs.all(), filters] as const,
    },
  },
};

// =============================================================================
// OPTIMISTIC UPDATE HELPERS
// =============================================================================

export interface OptimisticContext<T> {
  previousData: T | undefined;
}

/**
 * Helper to create optimistic update handlers for list mutations
 * Handles add, update, and delete operations optimistically
 */
export function createOptimisticListMutation<TItem extends { id: string }>() {
  return {
    /**
     * Optimistically add an item to a list
     */
    add: <TData extends TItem[]>(
      queryClient: QueryClient,
      queryKey: readonly unknown[],
      newItem: TItem,
    ): OptimisticContext<TData> => {
      // Cancel any outgoing refetches
      queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<TData>(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData<TData>(queryKey, (old) => {
        if (!old) return [newItem] as TData;
        return [newItem, ...old] as TData;
      });

      return { previousData };
    },

    /**
     * Optimistically update an item in a list
     */
    update: <TData extends TItem[]>(
      queryClient: QueryClient,
      queryKey: readonly unknown[],
      updatedItem: Partial<TItem> & { id: string },
    ): OptimisticContext<TData> => {
      queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<TData>(queryKey);

      queryClient.setQueryData<TData>(queryKey, (old) => {
        if (!old) return old;
        return old.map((item) =>
          item.id === updatedItem.id ? { ...item, ...updatedItem } : item,
        ) as TData;
      });

      return { previousData };
    },

    /**
     * Optimistically remove an item from a list
     */
    remove: <TData extends TItem[]>(
      queryClient: QueryClient,
      queryKey: readonly unknown[],
      itemId: string,
    ): OptimisticContext<TData> => {
      queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<TData>(queryKey);

      queryClient.setQueryData<TData>(queryKey, (old) => {
        if (!old) return old;
        return old.filter((item) => item.id !== itemId) as TData;
      });

      return { previousData };
    },

    /**
     * Rollback on error
     */
    rollback: <TData>(
      queryClient: QueryClient,
      queryKey: readonly unknown[],
      context: OptimisticContext<TData>,
    ) => {
      if (context.previousData !== undefined) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
  };
}

/**
 * Helper to create optimistic update handlers for detail mutations
 */
export function createOptimisticDetailMutation<TItem>() {
  return {
    /**
     * Optimistically update a detail item
     */
    update: (
      queryClient: QueryClient,
      queryKey: readonly unknown[],
      updatedFields: Partial<TItem>,
    ): OptimisticContext<TItem> => {
      queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<TItem>(queryKey);

      queryClient.setQueryData<TItem>(queryKey, (old) => {
        if (!old) return old;
        return { ...old, ...updatedFields };
      });

      return { previousData };
    },

    /**
     * Rollback on error
     */
    rollback: (
      queryClient: QueryClient,
      queryKey: readonly unknown[],
      context: OptimisticContext<TItem>,
    ) => {
      if (context.previousData !== undefined) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
  };
}

// =============================================================================
// INVALIDATION HELPERS
// =============================================================================

/**
 * Invalidate all queries related to an entity
 */
export function invalidateEntity(
  queryClient: QueryClient,
  entityKeys: { all: readonly unknown[] },
) {
  return queryClient.invalidateQueries({ queryKey: entityKeys.all });
}

/**
 * Invalidate multiple entities at once
 */
export function invalidateEntities(
  queryClient: QueryClient,
  entities: Array<{ all: readonly unknown[] }>,
) {
  return Promise.all(entities.map((entity) => invalidateEntity(queryClient, entity)));
}
